import React from "react";
import DirectoryItem from "./DirectoryItem";
import FileItem from "./FileItem";
import { getDirectoryChildren, sortFileItems } from "../../utils/pathUtils";

/**
 * Component to recursively render a directory tree
 * @param {Object} props - Component props
 * @param {Array} props.files - Array of all file objects
 * @param {Object} props.expandedFolders - Object mapping folder paths to expansion state
 * @param {Function} props.onFileSelect - Handler for file selection
 * @param {Function} props.onDirectoryToggle - Handler for directory expansion toggle
 * @param {string} props.selectedFile - Path of currently selected file
 * @param {Set} props.loadingDirectories - Set of directories currently being loaded
 * @param {string} props.loadingFile - Path of file currently being loaded
 * @param {string} props.rootPath - Root path to start rendering from
 * @param {number} props.level - Current indentation level
 * @returns {JSX.Element} The directory tree
 */
const DirectoryTree = ({
  files,
  expandedFolders,
  onFileSelect,
  onDirectoryToggle,
  selectedFile,
  loadingDirectories,
  loadingFile,
  rootPath,
  level = 0,
}) => {
  // Get and sort children of the current directory
  const children = getDirectoryChildren(files, rootPath);
  const sortedChildren = sortFileItems(children);

  if (sortedChildren.length === 0 && level === 0) {
    return (
      <div className="text-neutral-500 dark:text-neutral-400 p-3 text-xs">
        No files found in this project.
      </div>
    );
  }

  return (
    <>
      {sortedChildren.map((item) => {
        if (item.type === "directory") {
          const isExpanded = expandedFolders[item.path];
          const isLoading = loadingDirectories.has(item.path);

          return (
            <DirectoryItem
              key={item.path}
              directory={item}
              level={level}
              isExpanded={isExpanded}
              isSelected={selectedFile === item.path}
              isLoading={isLoading}
              onClick={onDirectoryToggle}
            >
              {isExpanded && (
                <DirectoryTree
                  files={files}
                  expandedFolders={expandedFolders}
                  onFileSelect={onFileSelect}
                  onDirectoryToggle={onDirectoryToggle}
                  selectedFile={selectedFile}
                  loadingDirectories={loadingDirectories}
                  loadingFile={loadingFile}
                  rootPath={item.path}
                  level={level + 1}
                />
              )}
            </DirectoryItem>
          );
        } else {
          return (
            <FileItem
              key={item.path}
              file={item}
              level={level}
              isSelected={selectedFile === item.path}
              isLoading={loadingFile === item.path}
              onClick={onFileSelect}
            />
          );
        }
      })}
    </>
  );
};

export default DirectoryTree;
